import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'

import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import API from '@/lib/api'
import { extractErrorCode } from '@/lib/api/errorUtils'

import { FileUpload } from '../components/FileUpload'
import { UploadExpenseFormSchema } from './schema'

export type UploadExpenseFormValues = z.infer<typeof UploadExpenseFormSchema>

type UploadExpenseResponse =
  | {
    status: 'ok'
    type: 'expense'
    id: string
    symbol: string
    uuid: string
    permalink: string
  }
  | {
    status: 'ok'
    type: 'simple'
    id: string
  }

export const UploadExpenseForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, {
    keyPrefix: 'expense.upload.form',
  })

  const form = useForm<UploadExpenseFormValues>({
    resolver: zodResolver(UploadExpenseFormSchema),
    defaultValues: {
      receivedAt: new Date().toISOString().split('T')[0],
      files: [],
    },
  })

  const onSubmit = (data: UploadExpenseFormValues) => {
    setIsSubmitting(true)

    const postData = {
      receivedAt: data.receivedAt,
      description: data.description,
      attachments: data.files,
    }

    API.post<UploadExpenseResponse>('/api/v2/simple-expenses', postData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      formSerializer: {
        indexes: true,
      },
    })
      .then((res) => {
        toast.success(t(res.data.type === 'expense' ? 'successExpense.title' : 'successSimple.title'), {
          duration: 8000,
          dismissible: true,
        })

        navigate(res.data.type === 'expense' ? `/expense/${res.data.symbol}` : `/expenses/`)
      })
      .catch((error) => {
        const code = extractErrorCode(error)

        if (code === 40013) {
          const existing = (
            error as {
              response?: {
                data?: {
                  existing?: {
                    type: 'expense' | 'simple'
                    symbol?: string | null
                  }
                }
              }
            }
          ).response?.data?.existing

          const path =
            existing?.type === 'expense' && existing.symbol
              ? `/expense/${existing.symbol}`
              : '/expenses/'

          toast.error(t('duplicate.title'), {
            duration: 8000,
            action: {
              label: t('duplicate.action.label'),
              onClick: () => navigate(path),
            },
          })

          return
        }

        toast.error(t('error.title'), {
          duration: 5000,
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const onError = (_errors: FieldErrors<UploadExpenseFormValues>) => { }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onError)}
        className="space-y-8"
      >
        <div className="space-y-6">
          <FormField
            name="files"
            render={() => (
              <FormItem>
                <FormLabel>{t('file.label')}</FormLabel>
                <FormControl>
                  <FileUpload
                    name="files"
                    multiple={true}
                    mode="append"
                    maxFiles={3}
                    maxSize={3 * 1024 * 1024}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            name="receivedAt"
            render={({ field, formState: { errors } }) => {
              return (
                <FormItem>
                  <FormLabel>{t('receivedAt.label')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                  {errors.receivedAt &&
                    (errors.receivedAt?.message === 'Required' ? (
                      <FormMessage>{t('receivedAt.error')}</FormMessage>
                    ) : (
                      <FormMessage />
                    ))}
                </FormItem>
              )
            }}
          />

          <FormField
            name="description"
            render={({ field, formState: { errors } }) => (
              <FormItem>
                <FormLabel>{t('description.label')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                {errors.description?.message === 'Required' && (
                  <FormMessage>{t('description.error')}</FormMessage>
                )}
              </FormItem>
            )}
          />

          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t pt-5">
          <p className="text-xs text-muted-foreground">Údaje z dokladu vytěžíme a náklad připravíme ke kontrole.</p>
          <Button type="submit" size="lg" disabled={isSubmitting}>
            {isSubmitting && <Loading className="w-4 h-4 mr-2" />}
            {t('submit.label')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
